import React, { FC, ReactNode, Children, ReactElement } from "react";

// UI
import { Container } from "@UI/Components/Container/Container";
import { Box, BoxProps } from "@UI/Components/Box/Box";
import { Stack } from "@UI/Components/Stack/Stack";
import { Heading, HeadingProps } from "@UI/Components/Heading/Heading";
import { Flex } from "@UI/Components/Flex/Flex";

import useMediaQuery from "@UI/hooks/useMediaQuery";
import {
  Wizard,
  WizardContent,
  WizardNav,
} from "@Shared/Components/Layouts/Wizard/Wizard";

// Freemium
import { FAQSection } from "@modules/CustomerSupport/Components/FAQSection/FAQSection";

interface Props {
  children: ReactNode;
}

type PaymentFlowLayoutComponent = FC<Props> & {
  Header?: FC<HeadingProps>;
  Content?: FC<BoxProps>;
  OperationSummary?: FC<BoxProps>;
  Nav?: FC<BoxProps>;
};

const Header: PaymentFlowLayoutComponent["Header"] = (props) => {
  return (
    <Heading
      textAlign={{ base: "left", lg: "center" }}
      flexGrow={1}
      {...props}
    />
  );
};

const OperationSummary: PaymentFlowLayoutComponent["OperationSummary"] = (
  props
) => {
  return <Box {...props} />;
};

const Content: PaymentFlowLayoutComponent["Content"] = (props) => {
  return <Box {...props} />;
};

const Nav: PaymentFlowLayoutComponent["Nav"] = (props) => {
  return <Box {...props} />;
};

const PaymentFlowLayout: PaymentFlowLayoutComponent = ({ children }) => {
  const { isMobile } = useMediaQuery();

  const slots = {
    nav: undefined,
    header: undefined,
    content: undefined,
    operationSummary: undefined,
  };

  Children.forEach(children, (child: ReactElement) => {
    if (child.type === Header) {
      slots.header = child;
    } else if (child.type === Content) {
      slots.content = child;
    } else if (child.type === OperationSummary) {
      slots.operationSummary = child;
    } else if (child.type === Nav) {
      slots.nav = child;
    }
  });

  return (
    <>
      <Wizard>
        <WizardNav>{slots.nav}</WizardNav>
        <WizardContent>
          <Container maxW={["container.lg", "container.md"]} padding="0">
            <Stack
              direction={["column", "column", "column-reverse"]}
              spacing={["6", "6", "8"]}
              marginBottom="5"
            >
              {slots.operationSummary}
              <Flex alignItems="center" justifyContent="space-between">
                {slots.header}
              </Flex>
            </Stack>
            {slots.content}
            {isMobile && (
              <Box marginTop="10">
                <FAQSection />
              </Box>
            )}
          </Container>
        </WizardContent>
      </Wizard>
      {!isMobile && (
        <Container
          marginY={[10, 10, 20]}
          maxW={["container.lg", "container.md", "container.lg"]}
        >
          <FAQSection />
        </Container>
      )}
    </>
  );
};

PaymentFlowLayout.Header = Header;
PaymentFlowLayout.Content = Content;
PaymentFlowLayout.OperationSummary = OperationSummary;
PaymentFlowLayout.Nav = Nav;

export { PaymentFlowLayout };
